/**
 * Admin-only dev menu. Mounted inside the user dropdown when the api
 * was started with DEBUG_TOOLS=1 (reflected via /api/me's `debugTools`)
 * AND the live caller is an admin. Both gates are enforced — the env-
 * flag gate is the load-bearing prod safety, the role gate is defence
 * in depth.
 *
 * On reset/restore success, polls /api/health every 500ms (max 30s)
 * and reloads the page on the first 200 so all in-memory React state
 * starts fresh against the swapped database.
 */
import { type FC, useEffect, useState } from "react";

import {
  type Savepoint,
  createSavepoint,
  listSavepoints,
  resetDebugDb,
  restoreSavepoint,
} from "../lib/debug-api";
import { notifyToast } from "./Toast";

export interface DebugMenuProps {
  debugTools: boolean;
  userRole: string;
  /** The authed user's id (from /api/me). Used to warn that reset wipes
   *  the current user when the caller isn't the bootstrap admin (id=1). */
  userId: number | null;
}

const BOOTSTRAP_ADMIN_ID = 1;

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 30_000;
/** The api drains for ~500ms before exit (see apps/api/src/db/reset.ts).
 *  Wait a bit longer than that before the first health probe so we don't
 *  hit the still-draining old process and reload prematurely. */
const RESTART_HEAD_START_MS = 1_500;

async function waitForHealthAndReload(): Promise<void> {
  await new Promise((r) => setTimeout(r, RESTART_HEAD_START_MS));
  const start = Date.now();
  while (Date.now() - start < POLL_TIMEOUT_MS) {
    try {
      const r = await fetch("/api/health");
      if (r.ok) {
        // After reset/restore the JWT subject may no longer exist. The
        // post-reload /api/me will 401 and the existing auth shell
        // redirects to /login — that's the intended UX, not a bug.
        window.location.reload();
        return;
      }
    } catch {
      /* api still down — keep polling */
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  notifyToast("Debug: timed out waiting for api to come back");
}

function fmtTs(id: string): string {
  // 2026-05-03T14-22-00Z → 2026-05-03 14:22 UTC
  const m = id.match(/^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})Z$/);
  if (!m) return id;
  return `${m[1]} ${m[2]}:${m[3]} UTC`;
}

export const DebugMenu: FC<DebugMenuProps> = ({ debugTools, userRole, userId }) => {
  const enabled = debugTools && userRole === "admin";
  const [savepoints, setSavepoints] = useState<Savepoint[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    listSavepoints()
      .then((list) => {
        if (!cancelled) setSavepoints(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (!enabled) return null;

  async function onReset(mode: "base" | "demo") {
    if (busy) return;
    const isBootstrap = userId === BOOTSTRAP_ADMIN_ID;
    let prompt: string;
    if (mode === "base") {
      prompt = isBootstrap
        ? "Reset database to base? This wipes all data; your bootstrap-admin login is preserved."
        : "Reset database to base? This wipes ALL users including yours — only the bootstrap admin (planner@dolmit.com) will remain. You will be logged out.";
    } else {
      prompt = isBootstrap
        ? "Reset database to demo? This wipes all data and re-seeds dev fixtures; your bootstrap-admin login is preserved."
        : "Reset database to demo? This wipes ALL users — only the bootstrap admin and the seeded dev fixtures will remain. You will be logged out.";
    }
    if (!window.confirm(prompt)) return;
    setBusy(true);
    try {
      await resetDebugDb(mode);
      notifyToast(`Restoring (${mode})…`);
      await waitForHealthAndReload();
    } catch (err) {
      notifyToast(`Debug reset failed: ${(err as Error).message}`);
      setBusy(false);
    }
  }

  async function onSavepoint() {
    if (busy) return;
    setBusy(true);
    try {
      await createSavepoint();
      const list = await listSavepoints();
      setSavepoints(list);
      notifyToast("Savepoint created");
    } catch (err) {
      notifyToast(`Savepoint failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function onRestore(id: string) {
    if (busy) return;
    const isBootstrap = userId === BOOTSTRAP_ADMIN_ID;
    const prompt = isBootstrap
      ? `Restore savepoint ${fmtTs(id)}? This wipes the current data; your bootstrap-admin login is preserved.`
      : `Restore savepoint ${fmtTs(id)}? This wipes the current data and may remove your user — only the bootstrap admin survives. You will be logged out.`;
    if (!window.confirm(prompt)) return;
    setBusy(true);
    try {
      await restoreSavepoint(id);
      notifyToast("Restoring…");
      await waitForHealthAndReload();
    } catch (err) {
      notifyToast(`Restore failed: ${(err as Error).message}`);
      setBusy(false);
    }
  }

  const itemStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: "6px 10px",
    border: "none",
    background: "transparent",
    textAlign: "left",
    cursor: busy ? "wait" : "pointer",
    fontSize: 12,
    fontFamily: "'Source Sans Pro',system-ui,sans-serif",
    color: "#263457",
  };

  return (
    <div data-testid="debug-menu" style={{ padding: "4px 0" }}>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          color: "#7d8aa0",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          padding: "6px 10px 4px",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
      >
        Debug
      </div>
      <button
        type="button"
        role="menuitem"
        onClick={() => onReset("base")}
        disabled={busy}
        style={itemStyle}
      >
        Reset to base
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => onReset("demo")}
        disabled={busy}
        style={itemStyle}
      >
        Reset to demo
      </button>
      <button type="button" role="menuitem" onClick={onSavepoint} disabled={busy} style={itemStyle}>
        Set savepoint
      </button>
      {savepoints.length > 0 && (
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            color: "#7d8aa0",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            padding: "6px 10px 2px",
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          }}
        >
          Restore savepoint
        </div>
      )}
      {savepoints.map((s) => (
        <button
          key={s.id}
          type="button"
          role="menuitem"
          onClick={() => onRestore(s.id)}
          disabled={busy}
          style={itemStyle}
        >
          {fmtTs(s.id)}
        </button>
      ))}
    </div>
  );
};
